import type { ReactNode } from 'react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Skeleton } from '../ui/Skeleton';
import { Spinner } from '../ui/Spinner';
import { FileUpload } from '../ui/FileUpload';
import { r2Url } from '../../utils/r2Url';
import type {
  ImageEditorExtraAction,
  ImageEditorFeatures,
  ImageEditorVariant,
  ImageRefDTO,
} from './types';

interface ImageEditorProps {
  /** Discriminated reference identifying the target image — primarily for
   *  the parent's bookkeeping; the editor itself doesn't issue API calls
   *  except to open the refine modal (which the parent renders). */
  imageRef: ImageRefDTO;
  /** Resolved R2 key for the current image; render-friendly URL is built
   *  internally as `/api/r2/<key>`. Null if no image yet. */
  imageR2Key: string | null;
  /** Friendly label inserted into button titles ("page 3", "the cover",
   *  "Rosie's reference"). */
  targetLabel: string;
  /** Locked targets disable generate/refine/variants. Only pages can lock. */
  isLocked?: boolean;
  /** Parent-controlled busy flag for the primary Generate button. */
  isGenerating?: boolean;
  /** Feature-flag gate for each affordance. Unset features render nothing. */
  features: ImageEditorFeatures;
  /** ── Variants pane state (parent-owned) ────────────────────────── */
  variantsOpen?: boolean;
  loadingVariants?: boolean;
  variants?: ImageEditorVariant[];
  /** ── Action callbacks. Each is invoked when the corresponding feature's
   *      button is clicked; the parent is responsible for calling the
   *      `image-editor/api.ts` helpers and refetching state on success. */
  onGenerate?: () => void;
  onOpenVariants?: () => void;
  onCloseVariants?: () => void;
  onSelectVariant?: (r2Key: string) => void;
  onRefine?: () => void;
  onEditPrompt?: () => void;
  onToggleLock?: () => void;
  onUpload?: (files: File[]) => void;
  onDelete?: () => void;
  /** Click-handler for the image itself when `features.gallery` is true. */
  onClickImage?: () => void;
  /** Optional extra actions rendered alongside the standard set
   *  (e.g. story-element refs offer "Amend description"). */
  extraActions?: ImageEditorExtraAction[];
  /** Slot above the image (badges, title). */
  header?: ReactNode;
  /** Slot below the image, before action buttons (text excerpt etc.). */
  body?: ReactNode;
  /** Custom placeholder shown when `imageR2Key` is null. */
  emptyState?: ReactNode;
  /** Optional disabled state on the delete button while a request runs. */
  deleting?: boolean;
  /** Variant size — compact thumbnails are used by `ImageEditorList`. */
  size?: 'default' | 'compact';
  /** Extra className applied to the root card. */
  className?: string;
}

/**
 * Single-image editor card used by every image surface in the app.
 *
 * Behaviour is identical across surfaces; what differs is which actions
 * the parent enables (`features`) and which slots it fills (`header`,
 * `body`). The component never owns state beyond DOM-local disclosure
 * (the variants pane visibility is parent-driven via `variantsOpen`).
 */
export function ImageEditor({
  imageR2Key,
  targetLabel,
  isLocked = false,
  isGenerating = false,
  features,
  variantsOpen = false,
  loadingVariants = false,
  variants = [],
  onGenerate,
  onOpenVariants,
  onCloseVariants,
  onSelectVariant,
  onRefine,
  onEditPrompt,
  onToggleLock,
  onUpload,
  onDelete,
  onClickImage,
  extraActions,
  header,
  body,
  emptyState,
  deleting = false,
  size = 'default',
  className,
}: ImageEditorProps) {
  const hasImage = !!imageR2Key;
  const imageSrc = imageR2Key ? r2Url(imageR2Key) : '';

  const rootClass =
    'image-editor' +
    (size === 'compact' ? ' image-editor--compact' : '') +
    (className ? ` ${className}` : '');

  return (
    <Card padding={size === 'compact' ? 'sm' : 'md'} className={rootClass}>
      {header && <div className="image-editor__header">{header}</div>}

      <div className="image-editor__image-area">
        {isGenerating ? (
          <Skeleton
            variant="rect"
            height={size === 'compact' ? '120px' : '200px'}
            className="image-editor__image-skeleton"
          />
        ) : hasImage ? (
          features.gallery && onClickImage ? (
            <button
              type="button"
              className="image-editor__image-button"
              onClick={onClickImage}
              aria-label={`View ${targetLabel} at full size`}
              title="View full size"
            >
              <img
                src={imageSrc}
                alt={targetLabel}
                className="image-editor__image"
              />
            </button>
          ) : (
            <img
              src={imageSrc}
              alt={targetLabel}
              className="image-editor__image"
            />
          )
        ) : (
          <div className="image-editor__image-placeholder">
            {emptyState ?? <span>{targetLabel}</span>}
          </div>
        )}
      </div>

      {body && <div className="image-editor__body">{body}</div>}

      <div className="image-editor__actions">
        {features.generate && onGenerate && (
          <Button
            variant={hasImage ? 'secondary' : 'primary'}
            size="sm"
            onClick={onGenerate}
            loading={isGenerating}
            disabled={isLocked}
            title={
              isLocked
                ? `Unlock ${targetLabel} to generate a new image`
                : hasImage
                  ? `Regenerate ${targetLabel} from the current prompt (replaces the existing image)`
                  : `Generate an illustration for ${targetLabel}`
            }
          >
            {hasImage ? 'Regenerate' : 'Generate'}
          </Button>
        )}

        {features.variants && onOpenVariants && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onOpenVariants}
            disabled={!hasImage || isLocked}
            title={
              !hasImage
                ? `Generate ${targetLabel} first to create variants`
                : isLocked
                  ? `Unlock ${targetLabel} to create variants`
                  : `Generate alternative versions of ${targetLabel} to compare and pick from`
            }
          >
            Variants
          </Button>
        )}

        {features.refine && onRefine && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefine}
            disabled={!hasImage || isLocked}
            title={
              !hasImage
                ? `Generate ${targetLabel} first to refine it`
                : isLocked
                  ? `Unlock ${targetLabel} to refine it`
                  : `Make targeted edits to ${targetLabel} by describing a specific change (keeps the overall composition)`
            }
          >
            Refine
          </Button>
        )}

        {features.editPrompt && onEditPrompt && (
          <Button variant="ghost" size="sm" onClick={onEditPrompt}>
            Edit Prompt
          </Button>
        )}

        {features.lock && onToggleLock && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleLock}
            title={
              isLocked
                ? `Unlock ${targetLabel} to allow regenerating, refining, or otherwise modifying it`
                : `Lock ${targetLabel} to prevent regenerating, refining, or otherwise modifying it`
            }
          >
            {isLocked ? 'Unlock' : 'Lock'}
          </Button>
        )}

        {extraActions?.map((action) => (
          <Button
            key={action.label}
            variant="ghost"
            size="sm"
            onClick={action.onClick}
            loading={action.loading}
            disabled={action.disabled}
            title={action.title}
          >
            {action.label}
          </Button>
        ))}

        {features.delete && onDelete && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            disabled={deleting}
            title={`Remove ${targetLabel}`}
          >
            {deleting ? 'Removing…' : 'Delete'}
          </Button>
        )}
      </div>

      {features.upload && onUpload && (
        <FileUpload
          accept="image/*"
          onFiles={onUpload}
          label={`Upload ${targetLabel}`}
          className="image-editor__upload"
        />
      )}

      {features.variants && variantsOpen && (
        <div className="image-editor__variants">
          <h4>Variants</h4>
          {loadingVariants ? (
            <div className="image-editor__variants-loading">
              <Spinner size="md" />
              <p>Generating variants...</p>
            </div>
          ) : (
            <div className="image-editor__variants-grid">
              {variants.map((variant, idx) => (
                <div
                  key={variant.r2_key}
                  className="image-editor__variant"
                  onClick={() => onSelectVariant?.(variant.r2_key)}
                >
                  <img
                    src={variant.image_url}
                    alt={`${targetLabel} variant ${idx + 1}`}
                    className="image-editor__variant-image"
                  />
                  <Button variant="ghost" size="sm">
                    Select
                  </Button>
                </div>
              ))}
            </div>
          )}
          <Button variant="ghost" size="sm" onClick={onCloseVariants}>
            Close Variants
          </Button>
        </div>
      )}
    </Card>
  );
}
